import { apiClient, getErrorMessage } from './client'
import type {
  User,
  UserStatus,
  AppProfile,
  AccessScopes,
  CreateUserRequest,
  CreateUserResponse,
  AdminPasskeyCredentialSummary,
} from '@paperair/shared-types'

export { getErrorMessage }
export type { AdminPasskeyCredentialSummary }

export interface UserWithAppCount extends User {
  appProfileCount: number
}

export interface UserSession {
  _id: string
  appId: string
  deviceInfo?: {
    userAgent?: string
    ip?: string
  }
  lastUsedAt: string
  createdAt: string
}

export interface UserDetailResponse {
  user: User
  appProfiles: AppProfile[]
  sessions: UserSession[]
}

export interface UsersListResponse {
  users: UserWithAppCount[]
  total: number
  page: number
  pages: number
}

export interface ListUsersParams {
  status?: UserStatus
  search?: string
  page?: number
  limit?: number
  sortBy?: string
  sortDir?: 'asc' | 'desc'
}

export async function listUsers(
  params: ListUsersParams = {}
): Promise<UsersListResponse> {
  const queryParams: Record<string, string> = {}

  if (params.status) {
    queryParams.status = params.status
  }
  if (params.search) {
    queryParams.search = params.search
  }
  if (params.page) {
    queryParams.page = params.page.toString()
  }
  if (params.limit) {
    queryParams.limit = params.limit.toString()
  }
  if (params.sortBy) {
    queryParams.sortBy = params.sortBy
  }
  if (params.sortDir) {
    queryParams.sortDir = params.sortDir
  }

  const response = await apiClient.get<UsersListResponse>('/admin/users', {
    params: queryParams,
  })
  return response.data
}

export async function getUser(id: string): Promise<UserDetailResponse> {
  const response = await apiClient.get<UserDetailResponse>(`/admin/users/${id}`)
  return response.data
}

export async function createUser(data: CreateUserRequest): Promise<CreateUserResponse> {
  const response = await apiClient.post<CreateUserResponse>('/admin/users', data)
  return response.data
}

export interface UpdateUserData {
  status?: string
  statusReason?: string
  profile?: Record<string, string>
  accessScopes?: AccessScopes
}

export async function updateUser(
  id: string,
  data: UpdateUserData
): Promise<User> {
  const response = await apiClient.patch<{ user: User }>(`/admin/users/${id}`, data)
  return response.data.user
}

export interface UpdateAppProfileData {
  role?: string
  isActive?: boolean
}

export async function unlockUser(id: string): Promise<User> {
  const response = await apiClient.post<{ user: User }>(`/admin/users/${id}/unlock`)
  return response.data.user
}

export async function updateAppProfile(
  profileId: string,
  data: UpdateAppProfileData
): Promise<AppProfile> {
  const response = await apiClient.patch<{ appProfile: AppProfile }>(
    `/admin/app-profiles/${profileId}`,
    data
  )
  return response.data.appProfile
}

export interface CreateAppProfileData {
  appId: string
  role: string
}

export async function createAppProfile(
  userId: string,
  data: CreateAppProfileData
): Promise<AppProfile> {
  const response = await apiClient.post<{ appProfile: AppProfile }>(
    `/admin/users/${userId}/app-profiles`,
    data
  )
  return response.data.appProfile
}

export async function listUserPasskeys(userId: string): Promise<AdminPasskeyCredentialSummary[]> {
  const response = await apiClient.get<{ passkeys: AdminPasskeyCredentialSummary[] }>(
    `/admin/users/${userId}/passkeys`
  )
  return response.data.passkeys
}

export async function deleteUserPasskey(userId: string, credentialId: string): Promise<void> {
  await apiClient.delete(`/admin/users/${userId}/passkeys/${encodeURIComponent(credentialId)}`)
}

export async function deleteAllUserPasskeys(userId: string): Promise<{ count: number }> {
  const response = await apiClient.delete<{ message: string; count: number }>(
    `/admin/users/${userId}/passkeys`
  )
  return { count: response.data.count }
}
